import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import {
  Card,
  Row,
  Col,
  Button,
  Typography,
  DatePicker,
  AutoComplete,
  Input,
  message,
  Table,
  Grid,
} from "antd";
import {
  ArrowLeftOutlined,
  PrinterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import commaNumber from "comma-number";
import _ from "lodash";

import { setFilters } from "./reserveSurplusLedgerSlice";
import { fetchReserveSurplusLedger } from "./reserveSurplusLedgerThunk";
import { currentDateStartTime, currentDateEndTime, dateFormat } from "../../../lib/functions";
import ReportDom from "../../../components/common/ReportDom";
import { getAccountBySeachApi } from "../../../apiservice/setting";
import RecordDateFilter from "../../../components/filters/RecordDateFilter";
import CommonDateRangeFilter from "../../../components/common/filters/CommonDateRangeFilter";
import "../../../components/common/bt.css";

const { Title } = Typography;
let format = commaNumber.bindWith(",", ".");

const ReserveSurplusLedgerReport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const reportRef = useRef();

  const { filters, data, openingBalance, closingBalance, loading } =
    useSelector((s) => s.reserveSurplusLedger);

  const [accounts, setAccounts] = useState([]);
  const [accLoading, setAccLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  // 🔹 handle account search directly
  const handleAccountSearch = async (val) => {
    if (!val || val.trim().length < 1) {
      setAccounts([]);
      return;
    }
    setAccLoading(true);
    try {
      const res = await getAccountBySeachApi({
        query: val,
        multiType: ["reserve_surplus"],
      });
      setAccounts(res.data || []);
    } catch (err) {
      console.error("Account search error:", err);
    } finally {
      setAccLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleReport = () => {
    if (!filters.account) {
      message.warning("Please select an account");
      return;
    }
    dispatch(fetchReserveSurplusLedger(filters));
  };

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "Reserve & Surplus Ledger",
    removeAfterPrint: true,
  });

  const handleNavigateToBack = () => {
    navigate("/reports");
  };

  const columns = [
    { title: "No.", render: (_, __, i) => i + 1 },
    {
      title: "Date",
      dataIndex: "created_date",
      render: (d) => dayjs(d).format(dateFormat),
    },
    { title: "Particular", dataIndex: "particular" },
    { title: "VCH No", dataIndex: "vch_no" },
    { title: "VCH Type", dataIndex: "vch_type" },
    {
      title: "Debit",
      dataIndex: "debit_amount",
      align: "right",
      render: (v) => format(Number(v || 0).toFixed(2)),
    },
    {
      title: "Credit",
      dataIndex: "credit_amount",
      align: "right",
      render: (v) => format(Number(v || 0).toFixed(2)),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      align: "right",
      render: (v) => format(Number(v || 0).toFixed(2)),
    },
  ];

  return (
    <div className="p-4">
      {/* Header */}
      <Card
        className="shadow-md rounded-xl mb-4"
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleNavigateToBack}
              style={{ marginRight: 8 }}
            />
            <Title level={4} style={{ margin: 0 }} className="reportBigTitle">
              Reserve & Surplus Ledger
            </Title>
          </div>
        }
        extra={
          <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
            { !screens.xs && "Print" }
          </Button>
        }
      />

      {/* Filters */}
      <Card className="shadow-md rounded-xl mb-4">
  <Row gutter={[16, 16]}>
    {/* Account Selector */}
    <Col xs={24} sm={12} md={8} lg={8}>
      <label>Account</label>
      <AutoComplete
        style={{ width: "100%" }}
        options={accounts.map((a) => ({
          label: `${a.acc_name} - ${a.acc_code || ""}`,
          value: a.acc_name,
          obj: a,
        }))}
        onSearch={handleAccountSearch}
        onSelect={(_, option) => handleFilterChange("account", option.obj)}
        placeholder="Search Account"
        notFoundContent={accLoading ? "Loading..." : "No accounts"}
        allowClear
      >
        <Input />
      </AutoComplete>
    </Col>    

    {/* <RecordDateFilter
      filters={filters}
      handleFilterChange={handleFilterChange}
    /> */}

    <CommonDateRangeFilter
  filters={filters}
  onChange={(fromDate, toDate) => {
	handleFilterChange("fromDate", fromDate);
	handleFilterChange("toDate", toDate);
  }}
/>

    {/* Report Button */}
    <Col xs={24} sm={12} md={4} lg={4} style={{ display: "flex", alignItems: "flex-end", height: 58 }}>
      <Button
        type="primary"
        icon={<SearchOutlined />}
        onClick={handleReport}
        loading={loading}
        style={{height: 35.18}}
        block
      >
        Report
      </Button>
    </Col>
  </Row>
</Card>


      {/* Report Preview */}
      <Card className="shadow-md rounded-xl">
        <Table
          dataSource={data}
          rowKey={(_, i) => i}
          columns={columns}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
          }}
          onChange={handleTableChange}
          bordered
          size="small"
          scroll={{ x: "max-content" }}
          summary={() => (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={5} align="right">
                  Opening Balance
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {format(openingBalance.toFixed(2))}
                </Table.Summary.Cell>
                <Table.Summary.Cell />
                <Table.Summary.Cell />
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={5} align="right">
                  Grand Total
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {format(_.sumBy(data, (r) => Number(r.debit_amount || 0)).toFixed(2))}
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {format(_.sumBy(data, (r) => Number(r.credit_amount || 0)).toFixed(2))}
                </Table.Summary.Cell>
                <Table.Summary.Cell />
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={5} align="right">
                  Closing Balance
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {format(closingBalance.toFixed(2))}
                </Table.Summary.Cell>
                <Table.Summary.Cell />
                <Table.Summary.Cell />
              </Table.Summary.Row>
            </>
          )}
        />
      </Card>

      {/* Print Layout (hidden) */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="Reserve & Surplus Ledger"
          mode="report-page"
          filters={filters}
          renderTable={() => {
            const debitTotal = _.sumBy(data, (r) => Number(r.debit_amount || 0));
            const creditTotal = _.sumBy(data, (r) => Number(r.credit_amount || 0));
            return (
              <table className="ledger-print-table">
                <colgroup>
                  <col style={{ width: "4%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "29%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "11%" }} />
                </colgroup>
                <tbody>
                  <tr className="ledger-print-heading">
                    <td>No.</td>
                    <td>Date</td>
                    <td>Particular</td>
                    <td>VCH No</td>
                    <td>VCH Type</td>
                    <td style={{ textAlign: "right" }}>Debit</td>
                    <td style={{ textAlign: "right" }}>Credit</td>
                    <td style={{ textAlign: "right" }}>Balance</td>
                  </tr>

                  <tr className="ledger-opening-row">
                    <td colSpan={7} style={{ textAlign: "right" }}>
                      Opening Balance
                    </td>
                    <td style={{ textAlign: "right" }}>{format(Number(openingBalance || 0).toFixed(2))}</td>
                  </tr>

                  {data.map((row, index) => (
                    <tr key={row.id || index}>
                      <td>{index + 1}</td>
                      <td>{row.created_date ? dayjs(row.created_date).format(dateFormat) : "-"}</td>
                      <td>{row.particular}</td>
                      <td>{row.vch_no}</td>
                      <td>{row.vch_type}</td>
                      <td style={{ textAlign: "right" }}>{format(Number(row.debit_amount || 0).toFixed(2))}</td>
                      <td style={{ textAlign: "right" }}>{format(Number(row.credit_amount || 0).toFixed(2))}</td>
                      <td style={{ textAlign: "right" }}>{format(Number(row.balance || 0).toFixed(2))}</td>
                    </tr>
                  ))}

                  <tr className="ledger-total-row">
                    <td colSpan={5} style={{ textAlign: "right" }}>Grand Total</td>
                    <td style={{ textAlign: "right" }}>{format(debitTotal.toFixed(2))}</td>
                    <td style={{ textAlign: "right" }}>{format(creditTotal.toFixed(2))}</td>
                    <td />
                  </tr>

                  <tr className="ledger-closing-row">
                    <td colSpan={7} style={{ textAlign: "right" }}>
                      Closing Balance
                    </td>
                    <td style={{ textAlign: "right" }}>{format(Number(closingBalance || 0).toFixed(2))}</td>
                  </tr>
                </tbody>
              </table>
            );
          }}
        />
      </div>
    </div>
  );
};

export default ReserveSurplusLedgerReport;
